import type { MergeFieldDef } from '@platform/shared-types';
import { chipClassesForGroup, DANGER_CHIP_CLASSES } from './mergeFieldColors';

/**
 * The pure half of "what should a `{{name}}` token's chip look like" — the
 * decision `BlockView.renderRichText` (read mode) and `MergeTextEditor`
 * (edit mode) both need to make the same way. Before this fix, read mode
 * rendered the raw `{{company_legal_name}}` token in a plain blue span while
 * edit mode already showed the catalog's plain-English label ("Company legal
 * name") in a group-coloured chip — two vocabularies for the same field.
 *
 * DELIBERATELY takes an already-resolved `MergeFieldDef | undefined` rather
 * than looking one up itself: only `MergeFieldDef`'s TYPE is imported here
 * (erased at compile time, exactly like `mergeFieldColors.ts`'s
 * `MergeFieldGroup` import), so this function has NO runtime dependency on
 * `@platform/shared-types` and is unit-testable with plain Vitest the same
 * way `mergeFieldColors.ts`, `diff.ts` and `mergeEditorActions.ts` are —
 * separately from "no React testing library", this package's standalone
 * `vitest run` (no Vite, no alias) cannot resolve `@platform/shared-types`'s
 * RUNTIME exports at all.
 *
 * The actual catalog lookup — `mergeFieldDef`, a RUNTIME export — is guarded
 * against a stale Vite pre-bundle (the same pattern `MergeFieldPalette.tsx`
 * uses for its own catalog reads) and lives at the point of use, in
 * `BlockView.tsx`, at module scope; it calls this function and is exercised
 * through the two consuming apps' Vite builds, not by a unit test here.
 */

export interface MergeFieldChipInfo {
  /** What the chip says: the catalog label, or the raw `{{name}}` token when unknown. */
  label: string;
  /** Tailwind classes for the chip's rest state (group colour, or danger/red). */
  chipClass: string;
  /** Tooltip text: `{{name}} — e.g. "example"` for a known field, a warning otherwise. */
  title: string;
  /** Whether `name` resolved to a catalog entry. */
  known: boolean;
}

/**
 * Given an already-resolved catalog def (or `undefined` for a name that
 * isn't, or is no longer, in the catalog), decide what the chip shows. A name
 * that is not (or no longer) in the catalog falls back to the danger/red
 * style showing the raw token — an unknown token is exactly what an author
 * needs to see, so it must stay visible, never vanish, never throw.
 */
export function chipInfoForDef(def: MergeFieldDef | undefined, name: string): MergeFieldChipInfo {
  if (!def) {
    return {
      label: `{{${name}}}`,
      chipClass: DANGER_CHIP_CLASSES,
      title: `Unknown merge field {{${name}}} — this will fail at render`,
      known: false,
    };
  }
  return {
    label: def.label,
    chipClass: chipClassesForGroup(def.group),
    title: `{{${name}}} — e.g. "${def.example}"`,
    known: true,
  };
}
